import React, { useState } from 'react';
import { Plus, Trash2, Eye, GripVertical } from 'lucide-react';
import {
  DndContext, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../lib/api';
import Modal from '../admin/Modal';
import ConfirmDialog from '../admin/ConfirmDialog';
import TemplateEditor from '../admin/TemplateEditor';
import TemplatePreviewPane from '../admin/TemplatePreviewPane';
import EmptyState from './EmptyState';

export default function StepEditor({ campaignId, steps, onChange }) {
  const [previewStep, setPreviewStep] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addStep = async (data) => {
    const res = await api.post(`/api/admin/outreach/campaigns/${campaignId}/steps`, data);
    onChange?.([...steps, res.step]);
    setAddOpen(false);
  };

  const editStep = async (step, data) => {
    const res = await api.patch(
      `/api/admin/outreach/campaigns/${campaignId}/steps/${step.stepIndex}`,
      data,
    );
    const updated = res?.step;
    if (updated) {
      onChange?.(steps.map((s) => (s.id === step.id ? updated : s)));
    }
    setEditingStep(null);
  };

  const handleDelete = async (step) => {
    const prev = steps;
    const optimistic = steps
      .filter((s) => s.id !== step.id)
      .map((s, i) => ({ ...s, stepIndex: i }));
    onChange?.(optimistic);
    try {
      await api.delete(`/api/admin/outreach/campaigns/${campaignId}/steps/${step.stepIndex}`);
      setError('');
    } catch (err) {
      onChange?.(prev);
      setError(err?.data?.error || err?.message || 'Failed to delete step');
      throw err;
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = steps.findIndex((s) => s.id === active.id);
    const newIndex = steps.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const prev = steps;
    const reordered = arrayMove(steps, oldIndex, newIndex).map((s, i) => ({ ...s, stepIndex: i }));
    onChange?.(reordered);
    try {
      const res = await api.post(
        `/api/admin/outreach/campaigns/${campaignId}/steps/reorder`,
        { order: reordered.map((s) => s.id) },
      );
      if (res?.steps) onChange?.(res.steps);
      setError('');
    } catch (err) {
      onChange?.(prev);
      setError(err?.data?.error || err?.message || 'Failed to reorder steps');
    }
  };

  return (
    <div className="space-y-2" data-testid="step-editor">
      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2" data-testid="step-editor-error">
          {error}
        </div>
      )}
      {steps.length === 0 ? (
        <EmptyState title="No steps" description="Add the first email in the sequence." action={
          <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900">
            <Plus className="w-3 h-3" /> Add step
          </button>
        } />
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {steps.map((s) => (
                <SortableStepRow
                  key={s.id}
                  step={s}
                  onEdit={() => setEditingStep(s)}
                  onPreview={() => setPreviewStep(s)}
                  onDelete={() => setConfirmDelete(s)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.03]">
            <Plus className="w-3 h-3" /> Add step
          </button>
        </>
      )}

      {addOpen && (
        <StepFormModal
          mode="create"
          campaignId={campaignId}
          onClose={() => setAddOpen(false)}
          onSubmit={addStep}
        />
      )}
      {editingStep && (
        <StepFormModal
          mode="edit"
          campaignId={campaignId}
          initial={editingStep}
          onClose={() => setEditingStep(null)}
          onSubmit={(data) => editStep(editingStep, data)}
        />
      )}
      {previewStep && (
        <PreviewModal
          step={previewStep}
          campaignId={campaignId}
          onClose={() => setPreviewStep(null)}
        />
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete step?"
        description={
          confirmDelete
            ? `Step ${confirmDelete.stepIndex} "${confirmDelete.subjectTemplate}" will be removed and subsequent steps will shift up.`
            : ''
        }
        confirmLabel="Delete step"
        destructive
        onConfirm={async () => {
          if (confirmDelete) await handleDelete(confirmDelete);
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function SortableStepRow({ step, onEdit, onPreview, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="rounded-xl border border-white/[0.06] bg-white/[0.02]" data-testid={`step-row-${step.id}`}>
      <div className="p-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            aria-label="Drag to reorder"
            className="p-1 rounded text-gray-600 hover:text-white cursor-grab active:cursor-grabbing touch-none"
            data-testid={`step-drag-handle-${step.id}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
          <div className="min-w-0">
            <div className="text-[11px] text-gray-500">Step {step.stepIndex} · {step.delayDays}d delay</div>
            <div className="text-[13px] text-white mt-0.5">{step.subjectTemplate}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onPreview} title="Preview"
            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]">
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button onClick={onEdit}
            data-testid={`step-edit-${step.id}`}
            className="text-[11px] text-gray-400 hover:text-white px-2">
            edit
          </button>
          <button onClick={onDelete} aria-label={`Delete step ${step.stepIndex}`}
            data-testid={`step-delete-${step.id}`}
            className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StepFormModal({ mode = 'create', campaignId, initial, onClose, onSubmit }) {
  // Sprint K — shared form for both creating new steps and editing
  // existing ones. The body uses TemplateEditor (TipTap WYSIWYG +
  // token picker + live preview) so operators don't author raw HTML.
  const isEdit = mode === 'edit';
  const [delay, setDelay] = useState(initial?.delayDays ?? 0);
  const [subject, setSubject] = useState(initial?.subjectTemplate ?? 'Checking in on {{account.name}}');
  const [html, setHtml] = useState(initial?.bodyTemplateHtml ?? '<p>Hi {{contact.name}},</p><p></p><p>— NexSav</p>');
  const [text, setText] = useState(initial?.bodyTemplateText ?? 'Hi {{contact.name}},\n\n\n— NexSav');
  const [customTokens, setCustomTokens] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      await onSubmit({
        delayDays: Number(delay),
        subjectTemplate: subject,
        bodyTemplateHtml: html,
        bodyTemplateText: text,
      });
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal
      open
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? `Edit email step${initial ? ` ${initial.stepIndex}` : ''}` : 'Add email step'}
      maxWidth="max-w-7xl"
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <button onClick={onClose} disabled={submitting}
            className="px-4 py-2 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting || !subject.trim()}
            className="px-5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50 shadow-lg shadow-[#B0FC38]/20">
            {submitting ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save changes' : 'Add step')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">
            {error}
          </div>
        )}
        <label className="block max-w-xs">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">Delay before this step</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              value={delay}
              onChange={(e) => setDelay(e.target.value)}
              className="w-24 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            />
            <span className="text-[12px] text-gray-500">days after the previous step</span>
          </div>
        </label>

        <TemplateEditor
          subject={subject}
          onSubjectChange={setSubject}
          bodyHtml={html}
          onBodyHtmlChange={setHtml}
          bodyText={text}
          onBodyTextChange={setText}
          customTokens={customTokens}
          onAddCustomToken={(t) => setCustomTokens((prev) => [...prev, t])}
          campaignId={campaignId}
        />
      </div>
    </Modal>
  );
}

function PreviewModal({ step, campaignId, onClose }) {
  return (
    <Modal open onClose={onClose} title={`Preview · step ${step.stepIndex}`} maxWidth="max-w-4xl">
      <div className="space-y-3">
        <div className="text-[11px] text-gray-500">
          Rendered against an eligible recipient. Use the "Preview as" dropdown to switch accounts.
        </div>
        <TemplatePreviewPane
          subjectTemplate={step.subjectTemplate}
          bodyHtml={step.bodyTemplateHtml}
          campaignId={campaignId}
          customTokens={[]}
        />
        <details className="rounded-lg border border-white/[0.06] bg-white/[0.02]">
          <summary className="cursor-pointer px-3 py-2 text-[11px] text-gray-400 uppercase tracking-wider">
            Plaintext fallback
          </summary>
          <pre className="px-3 pb-3 text-[12px] text-gray-300 whitespace-pre-wrap font-mono">
            {step.bodyTemplateText || ''}
          </pre>
        </details>
      </div>
    </Modal>
  );
}

